'use client';

import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Footer from '@/components/common/Footer';
import Link from 'next/link';
import Navbar from '@/components/common/Navbar';

interface TeamMember {
  id: string;
  name: string;
  role: string;
  bio: string;
  imageUrl: string;
  imageAlt: string;
}

const teamMembers: TeamMember[] = [
{
  id: 'team_1',
  name: 'Sarah Johnson',
  role: 'Founder & CEO',
  bio: 'With over 15 years in the cleaning industry, Sarah founded Gelle Cleaning to bring eco-friendly, professional cleaning services to the Twin Cities.',
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1de82ac24-1763301372736.png",
  imageAlt: 'Sarah Johnson, Founder and CEO of Gelle Cleaning LLC'
},
{
  id: 'team_2',
  name: 'Michael Chen',
  role: 'Operations Manager',
  bio: 'Michael ensures every cleaning meets our high standards. His attention to detail and commitment to excellence drives our quality.',
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_17a991d13-1763295980173.png",
  imageAlt: 'Michael Chen, Operations Manager at Gelle Cleaning LLC'
},
{
  id: 'team_3',
  name: 'Emily Rodriguez',
  role: 'Lead Cleaning Specialist',
  bio: 'Emily leads our residential cleaning team with passion and expertise. Her dedication to customer satisfaction is unmatched.',
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_19432b99a-1771540347796.png",
  imageAlt: 'Emily Rodriguez, Lead Cleaning Specialist at Gelle Cleaning LLC'
}];


const values = [
{
  id: 'value_1',
  icon: 'SparklesIcon' as const,
  title: 'Excellence',
  description: 'We strive for perfection in every cleaning job, ensuring your space is spotless and fresh.'
},
{
  id: 'value_2',
  icon: 'ShieldCheckIcon' as const,
  title: 'Trust',
  description: 'All our team members are background-checked, insured, and bonded for your peace of mind.'
},
{
  id: 'value_3',
  icon: 'HeartIcon' as const,
  title: 'Care',
  description: 'We treat every home and office as if it were our own, with respect and attention to detail.'
},
{
  id: 'value_4',
  icon: 'GlobeAltIcon' as const,
  title: 'Sustainability',
  description: 'Our eco-friendly products protect your family and the environment without compromising quality.'
}];


export default function AboutUsPage() {
  return (
    <main className="min-h-screen bg-gradient-to-b from-white to-green-50/30">
      <Navbar />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 to-green-800 text-white py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <Link href="/homepage" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
              <Icon name="ArrowLeftIcon" size={20} />
              <span>Back to Home</span>
            </Link>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">About Gelle Cleaning LLC</h1>
            <p className="text-xl text-white/90">Professional cleaning services with a personal touch, serving the Twin Cities since 2010.</p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 section-gradient-mid dot-pattern">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-foreground mb-6">Our Story</h2>
              <div className="space-y-4 text-lg text-muted-foreground">
                <p>
                  Gelle Cleaning LLC was founded in 2010 with a simple mission: to provide exceptional cleaning services that make life easier for busy families and businesses in the Twin Cities.
                </p>
                <p>
                  What started as a small team of dedicated cleaners has grown into one of the most trusted cleaning services in Minneapolis and St. Paul. Our commitment to quality, reliability, and eco-friendly practices has earned us over 1,200 satisfied customers and a 4.9-star rating.
                </p>
                <p>
                  Today, we continue to uphold the values that made us successful: attention to detail, respect for your space, and a genuine care for our customers and the environment.
                </p>
              </div>
            </div>
            <div className="relative h-96 rounded-xl overflow-hidden shadow-2xl">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_18f935249-1767041832890.png"
                alt="Gelle Cleaning team providing professional cleaning services"
                className="w-full h-full object-cover" />
              
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-br from-[#2E7D32]/10 via-green-50 to-[#4CAF50]/10">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <Icon name="FlagIcon" size={32} className="text-green-600" />
              </div>
              <h3 className="text-3xl font-bold text-foreground mb-4">Our Mission</h3>
              <p className="text-lg text-muted-foreground">
                To deliver exceptional cleaning services that exceed expectations while maintaining the highest standards of professionalism, reliability, and environmental responsibility.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <Icon name="EyeIcon" size={32} className="text-green-600" />
              </div>
              <h3 className="text-3xl font-bold text-foreground mb-4">Our Vision</h3>
              <p className="text-lg text-muted-foreground">
                To be the most trusted and preferred cleaning service in the Twin Cities, known for our commitment to quality, sustainability, and customer satisfaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 section-gradient-warm stripe-pattern">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">Our Core Values</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              These principles guide everything we do, from how we train our team to how we serve our customers.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) =>
            <div key={value.id} className="text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={value.icon} size={40} className="text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">{value.title}</h3>
                <p className="text-muted-foreground">{value.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#2E7D32] via-green-600 to-[#4CAF50] text-white">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Experience the Gelle Difference?</h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join over 1,200 satisfied customers who trust us with their cleaning needs.
          </p>
          <Link
            href="/homepage#booking"
            className="inline-flex items-center gap-2 bg-white text-green-600 px-8 py-4 rounded-lg font-semibold hover:bg-green-50 transition-all shadow-lg hover:shadow-xl">
            
            Book Your Cleaning Today
            <Icon name="ArrowRightIcon" size={20} />
          </Link>
        </div>
      </section>

      <Footer />
    </main>);

}